import { InputNumber, Table, Tag, Popconfirm } from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useState, useCallback } from 'react';
import { Button, Card, toast } from '@insight-health-ai/roseate/components';
import {
  getTrialConfigAction,
  updateTrialConfigAction,
  getAllFronteggRolesAction,
} from 'src/store/actions/userManagement.action';

const OPTrialManagementPage = () => {
  const [savedConfig, setSavedConfig] = useState({});
  const [draftConfig, setDraftConfig] = useState({});
  const [trialConfigLoading, setTrialConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [roleNameMap, setRoleNameMap] = useState({});

  const fetchTrialConfig = useCallback(async () => {
    setTrialConfigLoading(true);
    try {
      const [config, roles] = await Promise.all([
        getTrialConfigAction(),
        getAllFronteggRolesAction(),
      ]);
      setSavedConfig(config);
      setDraftConfig(config);
      const map = {};
      (Array.isArray(roles) ? roles : []).forEach((role) => {
        if (role.key) map[role.key] = role.name;
      });
      setRoleNameMap(map);
    } catch (error) {
      setSavedConfig({});
      setDraftConfig({});
      toast.error('Failed to load trial configuration');
    } finally {
      setTrialConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrialConfig();
  }, [fetchTrialConfig]);

  const hasUnsavedChanges =
    JSON.stringify(savedConfig) !== JSON.stringify(draftConfig);

  const trialConfigTableData = Object.entries(draftConfig).map(
    ([roleKey, days]) => ({
      key: roleKey,
      role_key: roleKey,
      trial_length_days: days,
    })
  );

  const handleDaysChange = (roleKey, newDaysValue) => {
    if (!newDaysValue || newDaysValue < 1) return;
    setDraftConfig((prev) => ({ ...prev, [roleKey]: newDaysValue }));
  };

  const handleDeleteRole = (roleKey) => {
    setDraftConfig((prev) => {
      const next = { ...prev };
      delete next[roleKey];
      return next;
    });
  };

  const handleSave = async () => {
    setConfigSaving(true);
    try {
      const entries = Object.entries(draftConfig).map(([key, days]) => ({
        role_key: key,
        trial_length_days: days,
      }));
      await updateTrialConfigAction(entries);
      setSavedConfig(draftConfig);
      toast.success('Trial configuration saved');
    } catch (error) {
      toast.error(
        `Failed to save: ${error?.response?.data?.message || error?.message}`
      );
    } finally {
      setConfigSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraftConfig(savedConfig);
  };

  const configColumns = [
    {
      title: 'Role',
      dataIndex: 'role_key',
      key: 'role_key',
      render: (roleKey) => (
        <Tag color="blue">{roleNameMap[roleKey] || roleKey}</Tag>
      ),
    },
    {
      title: 'Trial Length (days)',
      dataIndex: 'trial_length_days',
      key: 'trial_length_days',
      render: (days, record) => (
        <InputNumber
          min={1}
          max={365}
          value={days}
          onChange={(value) => handleDaysChange(record.role_key, value)}
          size="small"
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, record) => (
        <Popconfirm
          title="Remove this role from trial config?"
          onConfirm={() => handleDeleteRole(record.role_key)}
          okText="Remove"
          cancelText="Cancel"
        >
          <Button variant="text" appearance="danger" size="sm">
            <DeleteOutlined />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="flex-1 pt-4">
      <Card>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Trial Configuration
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Configure the default trial length per role
            </p>
          </div>
          {hasUnsavedChanges && (
            <span className="flex items-center gap-1.5 text-xs text-amber-600">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
              Unsaved changes
            </span>
          )}
        </div>

        <Table
          columns={configColumns}
          dataSource={trialConfigTableData}
          loading={trialConfigLoading}
          pagination={false}
          size="small"
          locale={{
            emptyText: 'No trial roles configured',
          }}
        />

        {hasUnsavedChanges && (
          <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-gray-100">
            <Button
              variant="solid"
              appearance="primary"
              size="sm"
              loading={configSaving}
              icon={<SaveOutlined />}
              onClick={handleSave}
            >
              Save configuration
            </Button>
            <Button
              variant="outlined"
              appearance="secondary"
              size="sm"
              onClick={handleDiscard}
              disabled={configSaving}
            >
              Discard
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OPTrialManagementPage;
